import { PICKUPS_GRID_FILTERS } from '../../../../../../modules/Dispatch/RouteExecution/Pickups/Grid/Filter/DEFAULT_constants';
import { getPickupsGridFilters } from '../../../../../../modules/Dispatch/RouteExecution/Pickups/Grid/Filter';
import { DEFAULT_PANEL_FILTER_VALUES } from '../../../constants';

type PickupRow = {
  pickupId?: string;
  groupId?: string;
};

type GridApi = {
  forEachNode?: (callback: (node: { data?: PickupRow; rowIndex?: number }) => void) => void;
  forEachNodeAfterFilterAndSort?: (
    callback: (node: { data?: PickupRow; rowIndex?: number }) => void
  ) => void;
  ensureIndexVisible?: (rowIndex: number, position?: string) => void;
  isColumnFilterPresent?: () => boolean;
  setFilterModel?: (model: null) => void;
  onFilterChanged?: () => void;
};

export type PickupGridFilterState = {
  panelFilter?: string;
  quickFilterText?: string;
};

type GlContainer = {
  _config?: { id?: string };
  parent?: {
    isStack?: boolean;
    getActiveContentItem?: () => unknown;
    parent?: unknown;
  };
  isHidden?: boolean;
};

// Resolves the header filter option used when a to-be-reviewed notification is clicked,
// from the client's filter list (pickupTypeFilter config).
export const getToBeReviewedGridFilterOption = (pickupTypeFilter?: string) =>
  getPickupsGridFilters(pickupTypeFilter).options.find(
    (filterOption) => filterOption.value === PICKUPS_GRID_FILTERS.toBeReviewed
  );

// Looks up the pickup from store data (used before select / expand on the Pickups grid).
export const findPickupById = (pickupsList: PickupRow[] = [], pickupId?: string) =>
  pickupsList.find((item) => item.pickupId?.toUpperCase() === pickupId?.toUpperCase());

// Matches driver-message behavior: notification click only works when Pickups grid is the active tab.
export const isActivePickupGridTab = (glContainer: GlContainer | undefined, pickupsGridId: string) => {
  const componentItem = glContainer?.parent;
  const stack = componentItem?.parent as GlContainer['parent'] & {
    isStack?: boolean;
    getActiveContentItem?: () => unknown;
  };
  if (glContainer?._config?.id !== pickupsGridId) return false;
  if (!stack?.isStack) return !glContainer?.isHidden;
  return stack.getActiveContentItem?.() === componentItem;
};

// Waits until the pickup row exists in the filtered grid before we select it after expand/filter.
export const isPickupVisibleInGrid = (gridApi: GridApi | undefined, pickupId?: string) => {
  if (!gridApi || !pickupId) return false;
  let isVisible = false;
  gridApi.forEachNodeAfterFilterAndSort?.((node) => {
    if (node.data?.pickupId?.toUpperCase() === pickupId.toUpperCase()) isVisible = true;
  });
  return isVisible;
};

// Normalizes groupId to match expandedGroups and group header row ids.
export const normalizeGroupId = (groupId?: string) => groupId?.toUpperCase() ?? '';

// Expands a collapsed group without mutating state so child pickup rows can be selected.
export const withGroupExpanded = (expandedGroups: Set<string>, groupId: string) => {
  const nextExpandedGroups = new Set(expandedGroups);
  nextExpandedGroups.add(normalizeGroupId(groupId));
  return nextExpandedGroups;
};

// Scrolls the selected pickup into view after notification navigation completes.
export const scrollPickupIntoView = (gridApi: GridApi | undefined, pickupId?: string) => {
  if (!gridApi || !pickupId) return;
  gridApi.forEachNode?.((node) => {
    if (node.data?.pickupId?.toUpperCase() === pickupId.toUpperCase()) {
      if (typeof node.rowIndex !== 'number') return;
      gridApi.ensureIndexVisible?.(node.rowIndex, 'middle');
    }
  });
};

// True when a status-bar (footer) panel filter is active — anything other than All/Unassigned.
export const isActivePanelFilter = (panelFilter?: string) =>
  Boolean(
    panelFilter &&
      !DEFAULT_PANEL_FILTER_VALUES.includes(
        panelFilter as (typeof DEFAULT_PANEL_FILTER_VALUES)[number]
      )
  );

// Skips re-applying the filter when To Be Reviewed is already active on notification click.
export const isToBeReviewedHeaderFilter = (headerFilterValue?: string | null) =>
  headerFilterValue === PICKUPS_GRID_FILTERS.toBeReviewed;

// Detects leftover quick/column filters that can still hide a pickup after panel + header are set.
export const hasQuickOrColumnFilters = (state: PickupGridFilterState, gridApi?: GridApi) =>
  Boolean(state.quickFilterText?.trim()) || Boolean(gridApi?.isColumnFilterPresent?.());

// Clears ag-Grid column filters so notification navigation can reveal the target pickup.
export const clearColumnFilters = (gridApi?: GridApi) => {
  if (!gridApi?.isColumnFilterPresent?.()) return false;
  gridApi.setFilterModel?.(null);
  gridApi.onFilterChanged?.();
  return true;
};
